import { useState } from 'react';

import { useAvailablePaymentMethods } from 'src/app/pages/promote/pageContext';
import type { PaymentType } from 'src/app/shared/utils/payments';

import { Paypal } from '../../components/PayPal';
import type { ExpandedPaymentMethod } from '../../components/shared/types';
import { StripeWallet } from '../../components/StripeWallet';
import { useSavedPaymentMethodExperiment } from '../useSavedPaymentMethodExperiment';

type InlinePaymentMethods = Exclude<PaymentType, ExpandedPaymentMethod | 'link'>;

const INLINE_PAYMENT_METHOD_COMPONENTS: Record<InlinePaymentMethods, React.ComponentType<Record<string, unknown>>> = {
	paypal: Paypal,
	applePay: StripeWallet,
	googlePay: StripeWallet,
};

const INLINE_PAYMENT_TYPES = ['applePay', 'googlePay', 'paypal'];

function isInlinePaymentMethod(type: PaymentType): type is InlinePaymentMethods {
	return INLINE_PAYMENT_TYPES.includes(type);
}

type Result = ModelHookResult<
	{
		inlinePaymentMethods: Array<{ type: InlinePaymentMethods; component: React.ComponentType }>;
		expandedMethod: ExpandedPaymentMethod | null;
		collapsible: boolean;
		displayExperiment: boolean;
	},
	{
		toggleExpanded: (method: ExpandedPaymentMethod) => void;
		setDisplayExperiment: (displaySavedPaymentMethodsExperiment: boolean) => void;
	}
>;

export function usePaymentMethods(): Result {
	const { paymentMethods } = useAvailablePaymentMethods();

	const [expandedMethod, setExpandedMethod] = useState<ExpandedPaymentMethod | null>(null);

	const {
		data: { displayExperiment },
		actions: { setDisplayExperiment },
	} = useSavedPaymentMethodExperiment();

	// When new payments will be added, this should be limited to 2 at most
	// because of the min width for paypal not allowing it to shrink
	// to fit more than 2 buttons in the same row
	const inlinePaymentMethods = paymentMethods.filter(isInlinePaymentMethod).map((type) => ({
		type,
		component: INLINE_PAYMENT_METHOD_COMPONENTS[type],
	}));

	return {
		data: {
			inlinePaymentMethods,
			expandedMethod,
			collapsible: paymentMethods.includes('sepa'),
			displayExperiment,
		},
		actions: {
			toggleExpanded: setExpandedMethod,
			setDisplayExperiment,
		},
	};
}
